// src/pages/Appli/AppliAdmin/users-admin/utils/usersExport.utils.js

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { toAbsoluteBadgeUrl } from "../../../../../services/validationBadgeServices";
import { toAbsoluteUserPhotoUrl } from "../../../../../services/userPhotoServices";

import {
  extractActeurs,
  extractClubs,
  getExcelColumnLetter,
  getRoleMeta,
  slugifyFilePart,
  uniq,
} from "./usersAdmin.utils";

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;

    reader.readAsDataURL(blob);
  });

const imageBlobToPngDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();

    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");

        canvas.width = image.naturalWidth || image.width || 300;
        canvas.height = image.naturalHeight || image.height || 300;

        const context = canvas.getContext("2d");

        context.drawImage(image, 0, 0);

        const pngDataUrl = canvas.toDataURL("image/png");

        URL.revokeObjectURL(objectUrl);

        resolve(pngDataUrl);
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        reject(error);
      }
    };

    image.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(error);
    };

    image.src = objectUrl;
  });

const fetchImageAsExcelBase64 = async (url) => {
  if (!url) return null;

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken") ||
    "";

  const response = await fetch(url, {
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : undefined,
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Impossible de charger l'image : ${response.status}`);
  }

  const blob = await response.blob();
  const mime = String(blob.type || "").toLowerCase();

  if (mime.includes("png")) {
    return {
      base64: await blobToDataUrl(blob),
      extension: "png",
    };
  }

  if (mime.includes("jpeg") || mime.includes("jpg")) {
    return {
      base64: await blobToDataUrl(blob),
      extension: "jpeg",
    };
  }

  if (mime.includes("gif")) {
    return {
      base64: await blobToDataUrl(blob),
      extension: "gif",
    };
  }

  return {
    base64: await imageBlobToPngDataUrl(blob),
    extension: "png",
  };
};

const buildExportRows = (users, badgesByUser) =>
  users.map((user) => {
    const clubs = uniq(
      extractClubs(user).map(
        (club) => club?.nom_club || club?.name || "",
      ),
    );

    const acteurs = uniq(
      extractActeurs(user).map((acteur) =>
        `${acteur?.nom || ""} ${acteur?.prenom || ""}`.trim(),
      ),
    );

    const badgeLinks = uniq(
      (badgesByUser[user.id] || [])
        .map((badge) =>
          badge?.badge_url
            ? toAbsoluteBadgeUrl(badge.badge_url)
            : "",
        )
        .filter(Boolean),
    );

    return {
      id: user.id,
      nom: user.nom || "",
      prenom: user.prenom || "",
      email: user.email || "",
      role: getRoleMeta(user.role_id).name,
      photoUrl: toAbsoluteUserPhotoUrl(user.photoURL) || "",
      clubsText: clubs.length ? clubs.join(" | ") : "—",
      acteursText: acteurs.length ? acteurs.join(" | ") : "—",
      badgeLinks,
    };
  });

export const exportUsersToExcel = async ({
  users,
  badgesByUser,
  selectedRole,
  roleOptions,
}) => {
  const workbook = new ExcelJS.Workbook();

  workbook.creator = "MonAppliLigue Nouvelle-Aquitaine";
  workbook.created = new Date();
  workbook.modified = new Date();

  const worksheet = workbook.addWorksheet("Utilisateurs", {
    views: [
      {
        state: "frozen",
        ySplit: 1,
      },
    ],
  });

  const exportRows = buildExportRows(users, badgesByUser);

  const maxBadges = Math.max(
    1,
    ...exportRows.map((row) => row.badgeLinks.length),
  );

  const badgeStartColumn = 8;

  const columns = [
    {
      header: "Nom",
      key: "nom",
      width: 18,
    },
    {
      header: "Prénom",
      key: "prenom",
      width: 18,
    },
    {
      header: "Email",
      key: "email",
      width: 32,
    },
    {
      header: "Rôle",
      key: "role",
      width: 24,
    },
    {
      header: "Photo",
      key: "photo",
      width: 14,
    },
    {
      header: "Clubs",
      key: "clubs",
      width: 38,
    },
    {
      header: "Acteurs",
      key: "acteurs",
      width: 36,
    },

    ...Array.from(
      {
        length: maxBadges,
      },
      (_, index) => ({
        header: `Badge ${index + 1}`,
        key: `badge_${index + 1}`,
        width: 20,
      }),
    ),
  ];

  worksheet.columns = columns;
  worksheet.properties.defaultRowHeight = 20;

  const headerRow = worksheet.getRow(1);

  headerRow.height = 24;

  headerRow.eachCell((cell) => {
    cell.font = {
      bold: true,
      color: {
        argb: "FFFFFFFF",
      },
    };

    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb: "FF013E8E",
      },
    };

    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
    };

    cell.border = {
      top: {
        style: "thin",
        color: {
          argb: "FFCBD5E1",
        },
      },
      left: {
        style: "thin",
        color: {
          argb: "FFCBD5E1",
        },
      },
      bottom: {
        style: "thin",
        color: {
          argb: "FFCBD5E1",
        },
      },
      right: {
        style: "thin",
        color: {
          argb: "FFCBD5E1",
        },
      },
    };
  });

  exportRows.forEach((item) => {
    const rowData = {
      nom: item.nom || "—",
      prenom: item.prenom || "—",
      email: item.email || "—",
      role: item.role || "—",
      photo: "",
      clubs: item.clubsText,
      acteurs: item.acteursText,
    };

    for (let index = 0; index < maxBadges; index += 1) {
      rowData[`badge_${index + 1}`] = "";
    }

    const row = worksheet.addRow(rowData);

    row.height = 64;

    row.eachCell(
      {
        includeEmpty: true,
      },
      (cell) => {
        cell.alignment = {
          vertical: "middle",
          wrapText: true,
        };

        cell.border = {
          top: {
            style: "thin",
            color: {
              argb: "FFE2E8F0",
            },
          },
          left: {
            style: "thin",
            color: {
              argb: "FFE2E8F0",
            },
          },
          bottom: {
            style: "thin",
            color: {
              argb: "FFE2E8F0",
            },
          },
          right: {
            style: "thin",
            color: {
              argb: "FFE2E8F0",
            },
          },
        };
      },
    );

    row.getCell("E").alignment = {
      vertical: "middle",
      horizontal: "center",
    };

    if (!item.badgeLinks.length) {
      row.getCell(badgeStartColumn).value = "—";
    } else {
      item.badgeLinks.forEach((url, index) => {
        const cell = row.getCell(badgeStartColumn + index);

        cell.value = {
          text: `Ouvrir badge ${index + 1}`,
          hyperlink: url,
          tooltip: url,
        };

        cell.font = {
          color: {
            argb: "FF1D4ED8",
          },
          underline: true,
        };

        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
        };
      });
    }
  });

  for (let index = 0; index < exportRows.length; index += 1) {
    const item = exportRows[index];
    const excelRowNumber = index + 2;

    const photoCell = worksheet.getCell(`E${excelRowNumber}`);

    if (!item.photoUrl) {
      photoCell.value = "—";
      continue;
    }

    try {
      const image = await fetchImageAsExcelBase64(item.photoUrl);

      if (!image?.base64 || !image?.extension) {
        throw new Error("Format d'image non reconnu.");
      }

      const imageId = workbook.addImage({
        base64: image.base64,
        extension: image.extension,
      });

      worksheet.addImage(imageId, {
        tl: {
          col: 4.18,
          row: excelRowNumber - 1 + 0.12,
        },
        ext: {
          width: 52,
          height: 52,
        },
        editAs: "oneCell",
      });
    } catch (error) {
      console.warn("Photo non intégrée dans l'export :", item.photoUrl, error);

      photoCell.value = {
        text: "Ouvrir photo",
        hyperlink: item.photoUrl,
        tooltip: item.photoUrl,
      };

      photoCell.font = {
        color: {
          argb: "FF1D4ED8",
        },
        underline: true,
      };
    }
  }

  const lastColumnLetter = getExcelColumnLetter(columns.length);

  worksheet.autoFilter = `A1:${lastColumnLetter}1`;

  worksheet.getCell("A1").note =
    `Export généré le ${new Date().toLocaleDateString("fr-FR")}`;

  const selectedRoleLabel =
    roleOptions.find((role) => role.id === selectedRole)?.label || "tous";

  const filename = `utilisateurs-filtrees-${slugifyFilePart(
    selectedRoleLabel,
  )}-${new Date().toISOString().slice(0, 10)}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    filename,
  );
};